import React, { useState } from "react";
import {
    Card,
    Typography,
    Box,
    Chip,
    Tooltip,
    Divider,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import dayjs from "dayjs";

import DemandeDetailsDialog from "./DemandeDetailsDialog";
import ChatPopup from "./CommentPopup";
import { getCommentaires } from "../../../../services/CommentaireService";
import { deleteDemande } from "../../../../services/DemandeService";
import { notifyError, notifySuccess } from "../../../../components/CustomToast";
import ConfirmDialog from "../components/ConfirmDialog";

const SoftIconButton = ({ color = "primary", disabled, children, sx, ...props }) => {
    const theme = useTheme();
    const palette = theme.palette[color] || theme.palette.primary;

    return (
        <button
            disabled={disabled}
            {...props}
            style={{
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: disabled ? "not-allowed" : "pointer",
            }}
        >
            <div
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    color: disabled ? theme.palette.text.disabled : palette.main,
                    backgroundColor: disabled
                        ? alpha(theme.palette.grey[500], 0.14)
                        : alpha(palette.main, 0.12),
                    border: `1px solid ${
                        disabled
                            ? alpha(theme.palette.grey[500], 0.24)
                            : alpha(palette.main, 0.28)
                    }`,
                    boxShadow: disabled ? "none" : `0 6px 18px ${alpha(palette.dark, 0.24)}`,
                    backdropFilter: "blur(2px)",
                    transition:
                        "transform .18s ease, box-shadow .18s ease, background-color .18s ease",
                }}
                onMouseEnter={(e) => {
                    if (disabled) return;
                    e.currentTarget.style.backgroundColor = alpha(palette.main, 0.16);
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = `0 10px 26px ${alpha(
                        palette.dark,
                        0.32
                    )}`;
                }}
                onMouseLeave={(e) => {
                    if (disabled) return;
                    e.currentTarget.style.backgroundColor = alpha(palette.main, 0.12);
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = `0 6px 18px ${alpha(
                        palette.dark,
                        0.24
                    )}`;
                }}
            >
                <span style={{ fontSize: 18, lineHeight: 0 }}>{children}</span>
            </div>
        </button>
    );
};

const norm = (s = "") =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const colorByStatus = (status = "") => {
    const s = norm(status);
    if (s === "livree") return "success";
    if (s === "refusee") return "error";
    if (s === "en attente") return "warning";
    return "default";
};

const labelByStatus = (status = "") => {
    const s = norm(status);
    if (s === "livree") return "Accepté";
    if (s === "refusee") return "Refusée";
    if (s === "en attente") return "En attente";
    return status || "—";
};

const DemandeAttestationCard = ({ demande, onDeleted }) => {
    const [openDetails, setOpenDetails] = useState(false);
    const [openPopup, setOpenPopup] = useState(false);
    const [messages, setMessages] = useState([]);
    const [collaborateurNom, setCollaborateurNom] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const user = JSON.parse(localStorage.getItem("userData"));

    const fetchCommentaires = async (id) => {
        try {
            const { data } = await getCommentaires(id);
            setCollaborateurNom(data.collaborateur || "Collaborateur");
            setMessages(
                (data.commentaires || []).map((c) => ({
                    contenu: c.contenu,
                    date: c.date,
                    auteurType: c.auteurType,
                }))
            );
        } catch {
            setMessages([]);
        }
    };

    const handleOpenPublicPopup = async (e) => {
        e.stopPropagation();
        await fetchCommentaires(demande.id);
        setOpenPopup(true);
    };

    const canDelete = norm(demande?.statut) === "en attente";

    const askDelete = (e) => {
        e.stopPropagation();
        if (!canDelete) return;
        setConfirmOpen(true);
    };

    const doDelete = async () => {
        try {
            setDeleting(true);
            const actorId = demande?.collaborateur || user?.uuid;
            await deleteDemande(demande.id, actorId);
            notifySuccess("Demande supprimée.");
            setConfirmOpen(false);
            onDeleted?.();
        } catch (err) {
            const msg =
                err?.response?.data?.error?.detail ||
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Suppression impossible.";
            notifyError(msg);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Card
                onClick={() => setOpenDetails(true)}
                variant="outlined"
                sx={{
                    px: 2.5,
                    py: 1.75,
                    mb: 1,
                    borderRadius: 2,
                    cursor: "pointer",
                    background:
                        "linear-gradient(90deg, rgba(249,249,249,0.95), rgba(236,239,241,0.95))",
                    "&:hover": { boxShadow: 2 },
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
                        <AssignmentTurnedInIcon sx={{ color: "primary.main" }} />
                        <Box minWidth={0}>
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                                {demande.sousType || "Attestation"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                                Demandée le {dayjs(demande.dateDemande).format("DD/MM/YYYY à HH:mm")}
                            </Typography>
                        </Box>
                    </Box>

                    <Box flex={1} display="flex" justifyContent="center">
                        <Chip
                            size="small"
                            label={labelByStatus(demande.statut)}
                            color={colorByStatus(demande.statut)}
                            sx={{ px: 1.5, fontWeight: 600, borderRadius: 999 }}
                        />
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Tooltip title="Ouvrir la discussion" arrow>
                            <SoftIconButton color="primary" onClick={handleOpenPublicPopup}>
                                <ChatBubbleOutlineIcon />
                            </SoftIconButton>
                        </Tooltip>

                        <Tooltip
                            title={
                                canDelete
                                    ? "Supprimer la demande"
                                    : 'Seules les demandes "en attente" peuvent être supprimées'
                            }
                            arrow
                        >
              <span>
                <SoftIconButton color="error" onClick={askDelete} disabled={!canDelete}>
                  <DeleteOutlineRoundedIcon />
                </SoftIconButton>
              </span>
                        </Tooltip>
                    </Box>
                </Box>

                {demande.description ? (
                    <>
                        <Divider sx={{ my: 1.25 }} />
                        <Typography variant="body2" color="text.secondary">
                            {demande.description}
                        </Typography>
                    </>
                ) : null}
            </Card>

            <ConfirmDialog
                open={confirmOpen}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={doDelete}
                loading={deleting}
                title="Supprimer la demande"
                description="Voulez-vous vraiment supprimer cette demande ? Cette action est définitive."
                confirmLabel="Supprimer"
                cancelLabel="Annuler"
            />

            <DemandeDetailsDialog
                open={openDetails}
                onClose={() => setOpenDetails(false)}
                demande={demande}
            />

            <ChatPopup
                open={openPopup}
                handleClose={() => setOpenPopup(false)}
                title={`Discussion - ${collaborateurNom}`}
                messages={messages}
                demandeId={demande.id}
                traiteePar={user?.uuid}
                onSend={() => fetchCommentaires(demande.id)}
            />
        </>
    );
};

export default DemandeAttestationCard;
